/**
 * Runtime configuration. One image serves every environment: the ingress serves
 * `/runtime-config.json` (from a ConfigMap); build-time `VITE_*` values are only defaults.
 */
/** A demo account offered by the account menu's "Switch user" (local/dev stacks only). */
export interface DemoUser {
  username: string;
  /** What this account is for, per language. */
  description?: { en: string; ar: string };
}

export interface RuntimeConfig {
  oidcAuthority: string;
  oidcClientId: string;
  /** Tenant preference for digits in Arabic: "latn" (default) or "arab". */
  numberingSystem: "latn" | "arab";
  /** `mail`: the local stack's test inbox (ADR-0060); absent elsewhere, so no link is shown. */
  embedded: { temporal: string; mlflow: string; grafana: string; mail?: string };
  docsUrl: string;
  /**
   * Demo accounts for "Switch user". Only the local stack's runtime config lists them; real
   * environments leave it empty and the menu entry is not shown.
   */
  demoUsers: DemoUser[];
}

export function defaultConfig(origin: string = typeof location !== "undefined" ? location.origin : ""): RuntimeConfig {
  const env = import.meta.env;
  return {
    oidcAuthority: env.VITE_OIDC_AUTHORITY || `${origin}/auth/realms/agent-platform`,
    oidcClientId: env.VITE_OIDC_CLIENT_ID || "agent-platform-ui",
    numberingSystem: env.VITE_NUMBERING_SYSTEM === "arab" ? "arab" : "latn",
    embedded: { temporal: "/embedded/temporal/", mlflow: "/embedded/mlflow/", grafana: "/embedded/grafana/" },
    docsUrl: "/docs/sdk/",
    demoUsers: [],
  };
}

let current: RuntimeConfig | null = null;

/** The configuration loaded at startup (defaults until `loadConfig` resolves). */
export function runtimeConfig(): RuntimeConfig {
  return current ?? defaultConfig();
}

export async function loadConfig(fetchImpl: typeof fetch = fetch): Promise<RuntimeConfig> {
  const base = defaultConfig();
  current = base;
  try {
    const res = await fetchImpl("/runtime-config.json", { headers: { Accept: "application/json" } });
    if (!res.ok || !(res.headers.get("content-type") ?? "").includes("json")) return base;
    const extra = (await res.json()) as Partial<RuntimeConfig>;
    current = {
      ...base,
      ...extra,
      embedded: { ...base.embedded, ...(extra.embedded ?? {}) },
      demoUsers: Array.isArray(extra.demoUsers) ? extra.demoUsers.filter((u) => typeof u?.username === "string") : [],
    };
    return current;
  } catch {
    return base;
  }
}
